// src/components/layout/Header.js - Header component for the application
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ThemeToggle from '../common/ThemeToggle';
import aransciaLogo from '../../assets/images/aranscia-logo.svg';

const HeaderContainer = styled.header`
  background-color: #17274f; /* Aranscia dark blue */
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-md) 0;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 46px; /* Ensure consistent header height */
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.5em; /* Minimum distance of an "a" character */
  align-items: end;
  grid-template-areas: "logo text";
  height: 30px; /* Match logo height */

  &:hover {
    text-decoration: none;
  }
`;

const LogoImage = styled.img`
  height: 30px;
  width: auto;
  grid-area: logo;
  align-self: end;
`;

const LogoText = styled.span`
  line-height: 1;
  grid-area: text;
  align-self: end;
  margin-bottom: 0;
`;

const Nav = styled.nav`
  display: flex;
  gap: var(--spacing-lg);
  align-self: center; /* Center the nav within the header */

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: var(--spacing-sm);
  border-bottom: 2px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  transition: border-color var(--transition-speed) ease;

  &:hover {
    text-decoration: none;
    border-color: var(--primary-light);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  align-self: center; /* Center the actions within the header */
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  font-size: 1.5rem;
  color: white;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <HeaderContainer>
      <div className="container">
        <HeaderContent>
          <Logo to="/">
            <LogoImage src={aransciaLogo} alt="Aranscia Logo" />
            <LogoText>{" PHI Training"}</LogoText>
          </Logo>
          
          <Nav>
            <NavLink to="/" active={isActive('/')}>Home</NavLink>
            <NavLink to="/tutorial" active={isActive('/tutorial')}>Tutorial</NavLink>
            <NavLink to="/reference" active={isActive('/reference')}>Reference</NavLink>
            <NavLink to="/learning" active={isActive('/learning')}>Learning</NavLink>
            <NavLink to="/resources" active={isActive('/resources')}>Resources</NavLink>
          </Nav>
          
          <HeaderActions>
            <ThemeToggle />
            <MobileMenuButton aria-label="Toggle menu">
              ☰
            </MobileMenuButton>
          </HeaderActions>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

export default Header; 